import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Users, Search, Check, X, UserPlus, ChevronRight } from "lucide-react";

export default function CoachDashboard() {
  const t = useT();
  const [players, setPlayers] = useState(null);
  const [links, setLinks] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  const load = async () => {
    const p = await api.get("/coach/players"); setPlayers(p.data);
    const l = await api.get("/coach-links"); setLinks(l.data);
  };
  useEffect(() => { load(); }, []);

  const search = async (q) => {
    setQuery(q);
    if (q.length < 2) return setResults([]);
    const { data } = await api.get(`/players/search?q=${encodeURIComponent(q)}`); setResults(data);
  };
  const invite = async (p) => { try { await api.post("/coach-links", { player_id: p.id }); toast.success(`Invite sent to ${p.name}`); setQuery(""); setResults([]); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const act = async (id, action) => { await api.put(`/coach-links/${id}`, { action }); load(); toast.success(`Request ${action}d`); };

  const pending = links.filter((l) => l.status === "pending" && l.initiator === "player");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-bold text-3xl flex items-center gap-2"><Users /> {t("My Players")}</h1>
      </div>

      {/* invite */}
      <div className="ct-card p-4" data-testid="invite-player">
        <div className="flex items-center gap-2 mb-2"><UserPlus size={16} className="text-brand" /><span className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("Invite a player")}</span></div>
        <div className="relative"><Search className="absolute left-3 top-2.5 text-slate-400" size={18} /><Input className="pl-10" value={query} onChange={(e) => search(e.target.value)} placeholder="Search player by name, email or username" data-testid="player-search" /></div>
        {results.length > 0 && <div className="mt-2 space-y-1">{results.map((p) => <button key={p.id} onClick={() => invite(p)} data-testid={`invite-${p.id}`} className="w-full text-left bg-slate-50 hover:bg-slate-100 rounded-lg p-2 flex justify-between items-center text-sm"><span><b>{p.name}</b> <span className="text-slate-500">@{p.username}</span></span><UserPlus size={14} /></button>)}</div>}
      </div>

      {pending.length > 0 && (
        <div className="ct-card p-4" data-testid="pending-requests">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("Pending requests")}</span>
          <div className="space-y-2 mt-2">
            {pending.map((l) => (
              <div key={l.id} className="flex items-center justify-between bg-amber-50 rounded-lg p-3">
                <span className="text-sm font-semibold">{l.player?.name} wants you as their coach</span>
                <div className="flex gap-1"><button onClick={() => act(l.id, "approve")} className="p-1.5 bg-green-100 text-green-700 rounded" data-testid={`accept-${l.id}`}><Check size={14} /></button><button onClick={() => act(l.id, "reject")} className="p-1.5 bg-red-100 text-brand rounded" data-testid={`decline-${l.id}`}><X size={14} /></button></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!players ? <div className="grid md:grid-cols-2 gap-4">{[1, 2].map((i) => <div key={i} className="skeleton h-32" />)}</div> : players.length === 0 ? (
        <div className="ct-card p-12 text-center" data-testid="coach-empty"><Users className="mx-auto text-slate-300 mb-3" size={40} /><p className="text-slate-500">No linked players yet. Invite one above, or wait for a player to tag you.</p></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="coach-player-list">
          {players.map((p) => (
            <Link key={p.link_id} to={`/coach/player/${p.player.id}`} className="ct-card p-5 hover:border-brand transition-colors" data-testid={`player-card-${p.player.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-ink text-white flex items-center justify-center font-head font-black text-xl overflow-hidden">{p.profile?.photo_url ? <img src={p.profile.photo_url} className="w-full h-full object-cover" alt="" /> : p.player.name[0]}</div>
                  <div><div className="font-head font-bold text-lg leading-tight">{p.player.name}</div><div className="text-xs text-slate-500">{p.profile?.role_type || "Player"} · {p.entry_count} entries</div></div>
                </div>
                <ChevronRight className="text-slate-300" />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Mini l="Runs" v={p.batting.runs} /><Mini l="Wkts" v={p.bowling.wickets} /><Mini l="Avg" v={p.batting.average ?? "-"} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
function Mini({ l, v }) { return <div className="bg-slate-50 rounded-lg py-2"><div className="stat-num text-xl tabular">{v}</div><div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{l}</div></div>; }
